import React from 'react';
import { useTranslation } from 'react-i18next';
import type * as monacoEditor from 'monaco-editor';
import { BeforeMount, Monaco, OnMount } from '@monaco-editor/react';

import { calculateSimpleDigest } from '../../../utils/stringUtil';
import CodeEditor from '../CodeEditor';
import {
  addValidationErrors,
  flattenErrorMessages,
  localizeModelMarkers,
  setupMarkersObservation,
} from '../CodeEditor/utils';
import { JSON_FILE_NAME, SCHEMA_FILE_NAME } from '../CodeEditor/const';
import { CodeEditorStatus } from '../CodeEditor/types';

export interface IJsonEditorProps {
  value: string;
  isFullscreen: boolean;
  readOnly?: boolean;
  codeFolder?: string;
  schema?: { [key: string]: unknown } | null;
  options?: monacoEditor.editor.IStandaloneEditorConstructionOptions;
  onChange?: (value?: string) => void;
  onValidate?: (errors: string[], status: CodeEditorStatus, value?: string) => void;
}

const JsonEditor: React.FC<IJsonEditorProps> = ({
  value,
  isFullscreen,
  readOnly = false,
  codeFolder,
  schema,
  options,
  onChange,
  onValidate,
}) => {
  const { t } = useTranslation();

  const schemaText = JSON.stringify(schema);
  const schemaDigest = calculateSimpleDigest(schemaText);
  const schemaFolder = `${codeFolder || '-'}/${schemaDigest}`;
  const codeUrl = `file://${schemaFolder}/${JSON_FILE_NAME}`;

  const beforeEditorMount: BeforeMount = (monaco: Monaco) => {
    if (!schema) {
      return;
    }

    const diagnosticsOptions = monaco.languages.json.jsonDefaults.diagnosticsOptions || {};
    const schemaUrl = `https://docs.elma-bot.ai/schemas/custom/${schemaFolder}/${SCHEMA_FILE_NAME}`;
    if (diagnosticsOptions.schemas?.some((info) => info.uri === schemaUrl)) {
      return;
    }

    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      ...diagnosticsOptions,
      validate: true,
      schemas: [
        ...(diagnosticsOptions.schemas || []),
        {
          uri: schemaUrl,
          fileMatch: [codeUrl],
          schema: flattenErrorMessages(addValidationErrors(schema, t), t),
        },
      ],
    });
  };

  const onEditorMount: OnMount = (editor, monaco) => {
    setupMarkersObservation(editor, monaco, (markers) => localizeModelMarkers(markers, t));
  };

  return (
    <CodeEditor
      key={schemaFolder}
      beforeEditorMount={beforeEditorMount}
      codeLanguage="json"
      codeUrl={codeUrl}
      isFullscreen={isFullscreen}
      options={options}
      readOnly={readOnly}
      value={value}
      onChange={onChange}
      onEditorMount={onEditorMount}
      onValidate={onValidate}
    />
  );
};

export default JsonEditor;
